import React, { useState, useEffect } from "react";
import styles from "./style.module.scss";
import baseStyles from "../../create-app/style.module.scss";
import Breadcrumbs from "../../../components/Breadcrumbs";
import { Formik } from "formik";
import * as yup from "yup";
import pricingValidation from "../../../../components/modals/add-new-application-modal/pricing-validation";
import Pricing from "../../create-app/components/pricing";
import Equipments from "../../create-app/components/equipments";
import Details from "../../create-app/components/details";
import Cost from "../../create-app/components/cost";
import Processors from "../../create-app/components/processor";
import TextInput from "../../../components/form/text-input";
import Select from "../../../components/form/select";
import SlidingToggle from "../../../components/form/sliding-checkbox";
import Footer from "./components/footer";
import {
  detailsInfoSchema,
  equipmentSchema,
  getDefaultFees,
  templateFormSchema,
  createPricingInfoSchema,
  processorInfoSchema,
} from "../../create-app/validation-schema";

import {
  GetMccList,
  getEmsOfficesById,
  saveTemplateV2,
  getTemplateById,
} from "../../../../api";
import { withRouter } from "react-router-dom";
import { toast, ToastBar, Toaster } from "react-hot-toast";
import SkeletonLoader from "../../../components/skeleton-loader";
import useOrganizationStore, {
  fetchPricingDefaults,
} from "../../../stores/organization";
import useUserStore from "../../../stores/user";
import { usePaginatedOfficeOptions } from "../../../hooks/usePaginatedOfficeOptions";

const createFormSchema = (pricingDefaults) =>
  yup.object({
    ...templateFormSchema.fields,
    ...createPricingInfoSchema(pricingDefaults).fields,
    ...equipmentSchema,
    ...detailsInfoSchema.fields,
    ...processorInfoSchema.fields,
  });

let dual_pricing_validation = pricingValidation.pricingValidation.dual_pricing;
const surcharge_validation = pricingValidation.pricingValidation.surcharge;
const flat_rate_validation = pricingValidation.pricingValidation.flat_rate;
const ic_plus_validation = pricingValidation.pricingValidation.ic_plus;
const tiered_validation = pricingValidation.pricingValidation.tiered;

const pricingInfoFields = [
  "pricing_model",
  "dual_pricing_rate",
  "dual_pricing_monthly_fee",
  "surcharge_rate",
  "surcharge_per_transaction",
  "surcharge_monthly_fee",
  "flat_rate",
  "flat_rate_transaction",
  "flat_rate_amex",
  "flat_rate_amex_transaction",
  "ic_plus_rate",
  "ic_plus_transaction_fee",
  "ic_plus_amex_rate",
  "ic_plus_amex_transaction_fee",
  "tiered_qual_rate",
  "tiered_qual_transaction",
  "tiered_qual_debit_rate",
  "tiered_qual_debit_per_transaction",
  "tiered_mid_qual_rate",
  "tiered_mid_qual_transaction",
  "tiered_mid_qual_debit_rate",
  "tiered_mid_qual_debit_per_transaction",
  "tiered_non_qual_rate",
  "tiered_non_qual_transaction",
  "tiered_non_qual_debit_rate",
  "tiered_non_qual_debit_per_transaction",
  "tiered_amex_structure",
  "amex_tier_one_rate",
  "amex_tier_one_fee",
  "amex_tier_two_rate",
  "amex_tier_two_fee",
  "amex_tier_three_rate",
  "amex_tier_three_fee",
  "amex_tier_four_rate",
  "amex_tier_four_fee",
  "tiered_amex_flat_rate",
  "tiered_amex_flat_transaction",
  "tiered_amex_ic_plus_rate",
  "tiered_amex_ic_plus_transaction",
  "pin_debit_network_fee",
  "debit_rate",
  "debit_per_txn",
  "ebt",
  "fnf_number",
  "ebt_transactions",
  "var_sheet_request",
  // "reprogram_hardware",
  // "use_existing_gateway",
  // "existing_gateway",
  "var_only",
];

const detailsInfoField = [
  "who_to_deploy_equipment",
  "who_to_pay_for_equipment",
  "who_to_deploy_gateway",
  "billing_sequence",
  "cover_cancellation",
  "reimbursement_maximum",
  "expedited_funding",
  "batch_time",
  "equipment_address",
  "invoice_address",
  // "special_instructions",
];

const unwrapId = (v) =>
  v && typeof v === "object" ? (v.value ?? "") : (v ?? "");

const optionalBlankPricingFields = [
  "tiered_mid_qual_rate",
  "tiered_mid_qual_transaction",
  "tiered_mid_qual_debit_rate",
  "tiered_mid_qual_debit_per_transaction",
  "tiered_non_qual_rate",
  "tiered_non_qual_transaction",
  "tiered_non_qual_debit_rate",
  "tiered_non_qual_debit_per_transaction",
  "amex_tier_two_rate",
  "amex_tier_two_fee",
  "amex_tier_three_rate",
  "amex_tier_three_fee",
  "amex_tier_four_rate",
  "amex_tier_four_fee",
];

function mergeTemplateDataIntoFormState(statePrev, apiData) {
  const stored = Object.fromEntries(
    Object.entries(apiData.data ?? {}).filter(([, v]) => v != null),
  );
  const loaded = { ...statePrev, ...stored };
  optionalBlankPricingFields.forEach((field) => {
    loaded[field] = apiData.data?.[field] ?? "";
  });

  loaded.name = apiData.name ?? "";
  loaded.north_template_id =
    apiData.north_template_id ?? loaded.north_template_id ?? "";
  loaded.is_editable = apiData.is_editable == 1 ? "yes" : "no";
  loaded.mcc_id = apiData.mcc_id ?? unwrapId(stored.mcc_id);
  loaded.ems_office_id =
    apiData.ems_office_id ?? unwrapId(stored.ems_office_id);
  loaded.pricing_model = stored.qualifiedtype ?? "dual_pricing";
  loaded.tiered_amex_structure =
    stored.tiered_amex_structure ?? stored.pricing_model ?? "tiered";
  if (!stored?.equipments) {
    loaded.equipments = [];
  } else if (Array.isArray(stored.equipments)) {
    loaded.equipments = stored.equipments;
  } else {
    loaded.equipments = Object.entries(stored.equipments).flatMap(
      ([equipmentType, equipmentGroups]) =>
        Object.entries(equipmentGroups ?? {}).flatMap(([_, equipmentList]) =>
          Array.isArray(equipmentList)
            ? equipmentList.map((equipment) => ({
                ...equipment,
                type: equipmentType,
              }))
            : [],
        ),
    );
  }

  return loaded;
}

const CreateTemplate = ({ match, history }) => {
  const breadcrumbItems = [
    { label: "Templates", to: "/v2/templates" },
    { label: "Create Template Pricing + Equipment" },
  ];
  const organizationId = useUserStore((state) => state.organizationId);
  const userLoading = useUserStore((state) => state.userLoading);

  const pricingDefaults = useOrganizationStore(
    (state) => state.pricingDefaults,
  );

  const pricingDefaultsLoading = useOrganizationStore(
    (state) => state.pricingDefaultsLoading,
  );

  const templateId = match.params.id;
  const [templateLoading, setTemplateLoading] = useState(!!templateId);
  const [savedEmsOffice, setSavedEmsOffice] = useState(null);
  const dataLoading = pricingDefaultsLoading || userLoading || templateLoading;

  useEffect(() => {
    if (organizationId) {
      fetchPricingDefaults(organizationId);
    }
  }, [organizationId]);

  useEffect(() => {
    if (!templateId) return;

    setTemplateLoading(true);
    getTemplateById(templateId)
      .then((res) => {
        if (res?.status && res?.data) {
          // Saved as { label, value }; the paged office list may not include it.
          const office = res.data.data?.ems_office_id;
          if (office?.label) setSavedEmsOffice(office);
          setFormData((prev) => mergeTemplateDataIntoFormState(prev, res.data));
          return;
        }

        toast.error(res?.message || "Template not found");
        history.push("/v2/templates");
      })
      .catch((error) => {
        console.error("Error loading template:", error);
        const message =
          error?.data?.message ||
          (error?.status === 403
            ? "You do not have permission to view this template"
            : "Failed to load template");
        toast.error(message);
        history.push("/v2/templates");
      })
      .finally(() => {
        setTemplateLoading(false);
      });
  }, [templateId]);

  const [mccList, setMccList] = useState([]);
  const {
    options: emsOfficeList,
    hasMore: hasMoreEmsOffices,
    loadingMore: isLoadingMoreEmsOffices,
    search: searchEmsOffices,
    loadMore: loadMoreEmsOffices,
  } = usePaginatedOfficeOptions(getEmsOfficesById);

  const [openSections, setOpenSections] = useState({
    processors: true,
    pricing: true,
    equipments: true,
    details: true,
  });

  useEffect(() => {
    GetMccList().then((res) => {
      if (res.success) {
        let mccList = res.data.map((item) => ({
          label: item.mcc
            ? `${item.mcc} - ${item.description}`
            : item.description,
          value: item.id,
        }));

        setMccList(mccList);
      }
    });
  }, []);

  const defaultFees = getDefaultFees(1);

  const [formData, setFormData] = useState({
    // template form fields
    name: "",
    north_template_id: "",
    mcc_id: "",
    ems_office_id: "",
    is_editable: "yes",

    // pricing fields
    pricing_model: "",
    // Dual pricing fields
    dual_pricing_rate: dual_pricing_validation.dual_pricing_rate.default,
    dual_pricing_monthly_fee:
      dual_pricing_validation.dual_pricing_monthly_fee.default.toFixed(2),

    // surcharge
    surcharge_rate: surcharge_validation.surcharge_rate.default.toFixed(2),
    surcharge_per_transaction:
      surcharge_validation.surcharge_per_transaction.default.toFixed(2),
    surcharge_monthly_fee:
      surcharge_validation.surcharge_monthly_fee.default.toFixed(2),

    // Flat Rate
    flat_rate: flat_rate_validation.flat_rate.default.toFixed(2),
    flat_rate_transaction:
      flat_rate_validation.flat_rate_transaction.default.toFixed(2),
    flat_rate_amex: flat_rate_validation.flat_rate_amex.default.toFixed(2),
    flat_rate_amex_transaction:
      flat_rate_validation.flat_rate_amex_transaction.default.toFixed(2),

    // IC+
    ic_plus_rate: ic_plus_validation.ic_plus_rate.default.toFixed(2),
    ic_plus_transaction_fee:
      ic_plus_validation.ic_plus_transaction_fee.default.toFixed(2),
    ic_plus_amex_rate: ic_plus_validation.ic_plus_amex_rate.default.toFixed(2),
    ic_plus_amex_transaction_fee:
      ic_plus_validation.ic_plus_amex_transaction_fee.default.toFixed(2),

    // tiered
    tiered_qual_rate: tiered_validation.tiered_qual_rate.default.toFixed(2),
    tiered_qual_transaction:
      tiered_validation.tiered_qual_transaction.default.toFixed(2),
    tiered_qual_debit_rate:
      tiered_validation.tiered_qual_debit_rate.default.toFixed(2),
    tiered_qual_debit_per_transaction:
      tiered_validation.tiered_qual_debit_per_transaction.default.toFixed(2),

    tiered_mid_qual_rate: "",
    tiered_mid_qual_transaction: "",
    tiered_mid_qual_debit_rate: "",
    tiered_mid_qual_debit_per_transaction: "",

    tiered_non_qual_rate: "",
    tiered_non_qual_transaction: "",
    tiered_non_qual_debit_rate: "",
    tiered_non_qual_debit_per_transaction: "",

    tiered_amex_structure: "tiered", // tiered, flat_rate, ic_plus

    amex_tier_one_rate: tiered_validation.amex_tier_one_rate.default.toFixed(2),
    amex_tier_one_fee: tiered_validation.amex_tier_one_fee.default.toFixed(2),
    amex_tier_two_rate: "",
    amex_tier_two_fee: "",
    amex_tier_three_rate: "",
    amex_tier_three_fee: "",
    amex_tier_four_rate: "",
    amex_tier_four_fee: "",

    tiered_amex_flat_rate:
      tiered_validation.tiered_amex_flat_rate.default.toFixed(2),
    tiered_amex_flat_transaction:
      tiered_validation.tiered_amex_flat_transaction.default.toFixed(2),

    tiered_amex_ic_plus_rate:
      tiered_validation.tiered_amex_ic_plus_rate.default.toFixed(2),
    tiered_amex_ic_plus_transaction:
      tiered_validation.tiered_amex_ic_plus_transaction.default.toFixed(2),

    // pin based debit
    pin_debit_network_fee: "yes",
    debit_rate: "2.00",
    debit_per_txn: "0.15",

    //Note: Below field is not used anywhere, but kept so as not to break anything.
    monthly_fee: "49.00",

    // ebt
    ebt: "no",
    fnf_number: "",

    // other default fees
    ...Object.keys(defaultFees).reduce((acc, key) => {
      acc[key] = defaultFees[key].default.toFixed(2);
      return acc;
    }, {}),

    var_only: {},

    equipments: [],
    other_gateway: "",
    processors: [],

    // details
    who_to_deploy_equipment: "",
    who_to_pay_for_equipment: "",
    who_to_deploy_gateway: "",
    billing_sequence: "daily",
    cover_cancellation: "no",
    reimbursement_maximum: "",
    expedited_funding: "no",
    batch_time: "06:00 PM",
    equipment_address: "dba_address",
    invoice_address: "agent_address",
    special_instructions: "",
  });

  const onSubmit = async (values, { setTouched, setSubmitting, setErrors }) => {
    const toastId = toast.loading("Saving template...", {
      duration: 10000,
    });

    try {
      setSubmitting(true);
      const dataToSubmit = { ...values };
      if (templateId) {
        dataToSubmit.id = templateId;
      }

      const res = await saveTemplateV2(dataToSubmit);

      if (res.status) {
        toast.success("Template saved successfully", {
          id: toastId,
        });
        history.push(`/templates`);
      } else {
        // Handle API error response
        const errorMessage = res.message || "Failed to save template";
        toast.error(errorMessage);

        // If there are validation errors from the API, set them
        if (res.errors) {
          setErrors(res.errors);
          // Mark all error fields as touched to show validation messages
          const touchedFields = Object.keys(res.errors).reduce((acc, key) => {
            acc[key] = true;
            return acc;
          }, {});
          setTouched(touchedFields);
        }
      }
    } catch (error) {
      console.error("Error saving template:", error);
      toast.error("An unexpected error occurred while saving the template");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.pageWrapper}>
      <Breadcrumbs items={breadcrumbItems} />

      <h1 className={styles.title}>Create Pricing + Equipment Template</h1>

      <Formik
        enableReinitialize
        validationSchema={createFormSchema(pricingDefaults)}
        onSubmit={onSubmit}
        initialValues={formData}
        validateOnBlur={true}
        validateOnChange={true}
      >
        {({
          handleSubmit,
          values,
          errors,
          touched,
          isSubmitting,
          handleChange,
          handleBlur,
          setFieldValue,
        }) => (
          <div className={styles.content}>
            <div className={styles.left}>
              <form
                className={styles.form}
                onSubmit={(e) => e.preventDefault()}
              >
                {dataLoading && (
                  <>
                    <SkeletonLoader
                      variant="rectangular"
                      component="div"
                      height="331px"
                      width="100%"
                      style={{ borderRadius: "24px" }}
                    />
                    {[0, 1, 2, 3].map((i) => (
                      <SkeletonLoader
                        key={i}
                        variant="rectangular"
                        component="div"
                        height="73px"
                        width="100%"
                        style={{ borderRadius: "24px", marginTop: "16px" }}
                      />
                    ))}
                  </>
                )}

                {!dataLoading && (
                  <>
                    <div className={baseStyles.section}>
                      <div
                        className={`${styles.grid} ${styles.gridCol1} ${styles.w100}`}
                      >
                        <TextInput
                          label="Template name"
                          name="name"
                          placeholder="Template name"
                          onChange={handleChange}
                          value={values.name}
                          onBlur={handleBlur}
                          touched={touched.name}
                          errored={errors.name}
                          error={errors.name}
                        />
                      </div>

                      <div
                        className={`${styles.grid} ${styles.gridCol1} ${styles.w100}`}
                      >
                        <TextInput
                          label="North Template ID"
                          name="north_template_id"
                          placeholder="North Template ID"
                          onChange={handleChange}
                          value={values.north_template_id ?? ""}
                          onBlur={handleBlur}
                          touched={touched.north_template_id}
                          errored={errors.north_template_id}
                          error={errors.north_template_id}
                        />
                      </div>

                      <div
                        className={`${styles.grid} ${styles.gridCol1} ${styles.w100}`}
                      >
                        <Select
                          label="MCC ID"
                          name="mcc_id"
                          placeholder="Select MCC ID"
                          options={mccList}
                          search
                          onChange={(value) => setFieldValue("mcc_id", value)}
                          value={values.mcc_id}
                          onBlur={handleBlur}
                          touched={touched.mcc_id}
                          errored={errors.mcc_id}
                          error={errors.mcc_id}
                        />
                      </div>

                      <div
                        className={`${styles.grid} ${styles.gridCol1} ${styles.w100}`}
                      >
                        <Select
                          label="KURV Office ID"
                          name="ems_office_id"
                          placeholder="Select KURV Office ID"
                          search
                          async
                          options={emsOfficeList}
                          prevSelected={savedEmsOffice}
                          onSearchChange={searchEmsOffices}
                          hasMore={hasMoreEmsOffices}
                          loadingMore={isLoadingMoreEmsOffices}
                          onLoadMore={loadMoreEmsOffices}
                          onChange={(value) =>
                            setFieldValue("ems_office_id", value)
                          }
                          value={values.ems_office_id}
                          onBlur={handleBlur}
                          touched={touched.ems_office_id}
                          errored={errors.ems_office_id}
                          error={errors.ems_office_id}
                        />
                      </div>

                      <div
                        className={`${styles.grid} ${styles.gridCol1} ${styles.w100}`}
                      >
                        <div className={styles.radioItem}>
                          <p className={styles.p_heading}>Editable</p>
                          <div className={styles.radios}>
                            <SlidingToggle
                              options={["yes", "no"]}
                              width="150px"
                              value={values.is_editable ?? "yes"}
                              onChange={(value) =>
                                setFieldValue("is_editable", value)
                              }
                            />
                          </div>
                          {errors.is_editable && touched.is_editable && (
                            <p className={styles.error}>{errors.is_editable}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    <Processors
                      isOpen={openSections.processors}
                      onToggle={() =>
                        setOpenSections((prev) => ({
                          ...prev,
                          processors: !prev.processors,
                        }))
                      }
                    />

                    <Pricing
                      pricingInfoFields={pricingInfoFields}
                      isOpen={openSections.pricing}
                      onToggle={() =>
                        setOpenSections((prev) => ({
                          ...prev,
                          pricing: !prev.pricing,
                        }))
                      }
                    />

                    <Equipments
                      isOpen={openSections.equipments}
                      onToggle={() =>
                        setOpenSections((prev) => ({
                          ...prev,
                          equipments: !prev.equipments,
                        }))
                      }
                    />

                    <Details
                      detailsInfoField={detailsInfoField}
                      isOpen={openSections.details}
                      onToggle={() =>
                        setOpenSections((prev) => ({
                          ...prev,
                          details: !prev.details,
                        }))
                      }
                    />

                    <Footer dataLoading={dataLoading} />
                  </>
                )}
              </form>
            </div>
            <div className={styles.right}>
              <Cost />

              <div className={styles.mobileFooter}>
                <Footer dataLoading={dataLoading} />
              </div>
            </div>
          </div>
        )}
      </Formik>

      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            borderRadius: "8px",
            background: "rgba(40, 39, 45, 0.85)",
            color: "#fff",
            padding: "16px 24px",
          },
          duration: 5000,
        }}
      >
        {(t) => (
          <ToastBar toast={t}>
            {({ icon, message }) => (
              <>
                {icon}
                {message}
                {t.type !== "loading" && (
                  <button
                    className={styles.toast_dismissal_button}
                    onClick={() => toast.dismiss(t.id)}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                    >
                      <path
                        d="M12.8535 12.1462C12.9 12.1927 12.9368 12.2478 12.962 12.3085C12.9871 12.3692 13.0001 12.4343 13.0001 12.5C13.0001 12.5657 12.9871 12.6307 12.962 12.6914C12.9368 12.7521 12.9 12.8073 12.8535 12.8537C12.8071 12.9002 12.7519 12.937 12.6912 12.9622C12.6305 12.9873 12.5655 13.0003 12.4998 13.0003C12.4341 13.0003 12.369 12.9873 12.3083 12.9622C12.2476 12.937 12.1925 12.9002 12.146 12.8537L7.99979 8.70685L3.85354 12.8537C3.75972 12.9475 3.63247 13.0003 3.49979 13.0003C3.36711 13.0003 3.23986 12.9475 3.14604 12.8537C3.05222 12.7599 2.99951 12.6327 2.99951 12.5C2.99951 12.3673 3.05222 12.24 3.14604 12.1462L7.29291 7.99997L3.14604 3.85372C3.05222 3.7599 2.99951 3.63265 2.99951 3.49997C2.99951 3.36729 3.05222 3.24004 3.14604 3.14622C3.23986 3.0524 3.36711 2.99969 3.49979 2.99969C3.63247 2.99969 3.75972 3.0524 3.85354 3.14622L7.99979 7.2931L12.146 3.14622C12.2399 3.0524 12.3671 2.99969 12.4998 2.99969C12.6325 2.99969 12.7597 3.0524 12.8535 3.14622C12.9474 3.24004 13.0001 3.36729 13.0001 3.49997C13.0001 3.63265 12.9474 3.7599 12.8535 3.85372L8.70666 7.99997L12.8535 12.1462Z"
                        fill="white"
                      />
                    </svg>
                  </button>
                )}
              </>
            )}
          </ToastBar>
        )}
      </Toaster>
    </div>
  );
};

export default withRouter(CreateTemplate);
